<template>
  <div v-show="false" />
</template>

<script>
import { defineComponent } from 'vue'
import { mapGetters, mapState } from 'vuex'

import ObjectivesInfoApiHandler from '@/api/okr-elements'
import { useOboardFullscreen } from '@/composables/fullscreen'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES } from '@/tracking/amplitude-helpers'
import { trackJiraIssueLinkedEvent } from '@/tracking/gtm-helpers'
import { COMPANY_SETTINGS_ENTITY_KEYS, OKR_ELEMENT_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { forgeCreateIssueModal } from '@/utils/forge-helpers/general'
import { NOTIFICATION_TYPES, showNotify } from '@/utils/notify'
import { OBJECTIVE_TYPES } from '@/utils/objective-types'
import { createGroupsList } from '@/utils/objectives'
import { useElementOwner } from '@/utils/okr-elements-forms-helpers'
import { getNavigationTabName } from '@/utils/tracking'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'
import { IS_DEVELOPMENT } from '@root/app-modes'

const objectivesApi = new ObjectivesInfoApiHandler()

export default defineComponent({
  name: 'CreateJiraIssue',
  props: {
    source: {
      type: String,
      default: ''
    }
  },

  emits: { save: null },

  setup() {
    const { createUsersListForPayload } = useElementOwner({
      [OKR_ELEMENT_ENTITY_KEYS.TYPE_ID]: OBJECTIVE_TYPES.TASK
    })

    const { exitFullscreen } = useOboardFullscreen()

    return {
      createUsersListForPayload,
      exitFullscreen
    }
  },

  computed: {
    ...mapState('system', {
      userData: state => state.userData,
      settings: state => state.settings
    }),

    ...mapGetters('objectives', {
      getLevelItem: 'getLevelItem'
    }),

    ...mapGetters('system', {
      userGroupsByWorkspace: 'userGroupsByWorkspace'
    })
  },

  methods: {
    /** @public */
    create(parentObjective) {
      if (IS_DEVELOPMENT) {
        showNotify({
          type: NOTIFICATION_TYPES.WARNING,
          content:
            'You are in <b>DEVELOPMENT</b> mode, creating Jira issue is <b>unavailable here</b>. But if You see this message it means that functionality is working properly. If You want to create Issue, please go to real Jira instance.',
          expanded: true,
          isHtml: true
        })
      }

      if (forgeCreateIssueModal) {
        const createIssueModal = new forgeCreateIssueModal({
          onClose: async ({ payload = [] } = {}) => {
            await this.onJiraIssuesCreated(payload, parentObjective)
          }
        })

        if (createIssueModal) {
          // Jira renders the modal on the host page, hidden behind the fullscreen app.
          this.exitFullscreen()
          createIssueModal.open()
        }
      }
    },

    async onJiraIssuesCreated(issues, parentObjective) {
      if (!issues || !issues.length) {
        return
      }
      const promises = []
      const ownerId = this.userData?.userAccountId || null
      const groups = this.userGroupsByWorkspace(parentObjective.workspaceId) || []

      issues.forEach(({ issueId }) => {
        const payload = {
          contribute: this.settings[COMPANY_SETTINGS_ENTITY_KEYS.TASK_CONTRIBUTE_BY_DEFAULT],
          platformElementIds: [issueId],
          parentId: parentObjective.id,
          users: this.createUsersListForPayload({ ownerId }),
          groups: createGroupsList(groups),
          workspaceId: parentObjective.workspaceId,
          dueDate: parentObjective.dueDate,
          intervalId: parentObjective.intervalId,
          levelId: this.getLevelItem('typeId', OBJECTIVE_TYPES.TASK, {
            [OKR_ELEMENT_KEYS.WORKSPACE_ID]: parentObjective[OKR_ELEMENT_KEYS.WORKSPACE_ID],
            [OKR_ELEMENT_KEYS.TYPE_ID]: OBJECTIVE_TYPES.TASK
          }).id
        }

        const promise = this.save(payload)
        promises.push(promise)
      })

      const objectives = await Promise.all(promises)
      this.$emit(
        'save',
        objectives.filter(objective => objective !== null)
      )
    },

    async save(payload) {
      let task = null
      const resolvedPayload = {
        ...payload,
        // undefined values needed to clear this fields in create request
        targetValue: undefined,
        originalValue: undefined,
        currentValue: undefined
      }
      try {
        task = await objectivesApi.createOkrElement(resolvedPayload)

        const [firstLinkedJiraIssue] = task.elements || []

        if (firstLinkedJiraIssue) {
          tracker.logEvent('Linked Jira-issue', {
            category: EVENT_CATEGORIES.OKR_MANAGEMENT,
            label: firstLinkedJiraIssue.issueType,
            event_ref: 'Plugin',
            source: this.source,
            contribution: firstLinkedJiraIssue[OKR_ELEMENT_KEYS.CONTRIBUTE] ? 'Yes' : 'No',
            tab: getNavigationTabName(this.$route),
            ent_id: firstLinkedJiraIssue[OKR_ELEMENT_KEYS.DISPLAY_ID]
          })

          trackJiraIssueLinkedEvent()
        }
      } catch (error) {
        handleError({ error })
      }

      return task
    }
  }
})
</script>

<style></style>
